import axios, { isAxiosError } from "axios";
import { MOSIDA_API_BASE_URL } from "../config/apiUrls";

const API_BASE_URL = MOSIDA_API_BASE_URL;
const favouritesApiUrl = `${API_BASE_URL}/favourites`;

function getAxiosErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data;
    if (typeof data === "string" && data.trim()) return data;
    if (data && typeof data === "object") {
      const message =
        (data as { message?: string }).message ||
        (data as { error?: string }).error;
      if (message) return String(message);
    }
    if (error.response?.status) {
      return `${fallback}: ${error.response.status} ${error.response.statusText || ""}`.trim();
    }
    return error.message || fallback;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

export interface CreateFavouritePayload {
  imageUrl?: string | null;
  image?: string | Blob | null;
  userId: string;
  /** Legacy / unused by form; prefer favouriteType for the API `type` field. */
  type?: string;
  /** Value sent as FormData `type` (e.g. station name). Defaults to "Caricature Hobby". */
  favouriteType?: string;
  ticketId?: string | null;
}

export async function createFavourite(payload: CreateFavouritePayload): Promise<{
  success: boolean;
  data?: any;
  error?: string;
}> {
  try {
    const formData = new FormData();
    formData.append("type", payload.favouriteType ?? "hobby caricature");
    formData.append("userId", payload.userId);

    if (payload.imageUrl) {
      formData.append("imageUrl", payload.imageUrl);
    }
    if (payload.image instanceof Blob) {
      formData.append("image", payload.image);
    } else {
      formData.append("image", payload.image ?? "null");
    }
    if (payload.ticketId) {
      formData.append("ticketId", payload.ticketId);
    }

    const response = await axios.post(favouritesApiUrl, formData, {
      headers: {
        accept: "*/*",
      },
    });

    return {
      success: true,
      data: response.data,
    };
  } catch (error: unknown) {
    return {
      success: false,
      data: isAxiosError(error) ? error.response?.data : undefined,
      error: getAxiosErrorMessage(error, "Failed to save favourite"),
    };
  }
}

export interface CreateFavouritesBulkItem {
  imageUrl: string;
  type: string;
  ticketId?: string | null;
}

export interface CreateFavouritesBulkPayload {
  accessToken: string;
  items?: CreateFavouritesBulkItem[];
  /** Builds `items` when omitted. */
  imageUrls?: string[];
  /** Applied to each built item; defaults to `"hobby caricature"`. */
  type?: string;
}

function buildBulkFavouriteItems(payload: CreateFavouritesBulkPayload): CreateFavouritesBulkItem[] {
  const itemType = payload.type?.trim() || "hobby caricature";

  const withTicket = (item: CreateFavouritesBulkItem): CreateFavouritesBulkItem => ({
    ...item,
  });

  if (payload.items?.length) {
    return payload.items
      .map((item) =>
        withTicket({
          imageUrl: item.imageUrl?.trim() || "",
          type: item.type?.trim() || itemType,
        })
      )
      .filter((item) => item.imageUrl.length > 0);
  }

  return (payload.imageUrls || [])
    .map((url) =>
      withTicket({
        imageUrl: typeof url === "string" ? url.trim() : "",
        type: itemType,
      })
    )
    .filter((item) => item.imageUrl.length > 0);
}

/** Save multiple character image URLs after login (hobby caricature flow). */
export async function createFavouritesBulk(payload: CreateFavouritesBulkPayload): Promise<{
  success: boolean;
  skipped?: boolean;
  data?: any;
  error?: string;
}> {
  const accessToken = payload.accessToken?.trim();
  if (!accessToken) {
    return { success: false, error: "Authentication token is missing." };
  }

  const items = buildBulkFavouriteItems(payload);
  if (items.length === 0) {
    return { success: true, skipped: true };
  }

  try {
    const response = await axios.post(
      `${favouritesApiUrl}/bulk`,
      { items },
      {
        headers: {
          accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      }
    );

    return {
      success: true,
      data: response.data,
    };
  } catch (error: unknown) {
    return {
      success: false,
      data: isAxiosError(error) ? error.response?.data : undefined,
      error: getAxiosErrorMessage(error, "Failed to save bulk favourites"),
    };
  }
}

export interface Favourite {
  id: string;
  imageUrl: string;
  type: string;
  userId: string;
  ticketId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FavouritesPagination {
  page: number;
  limit: number;
  total?: number;
  totalPages?: number;
  hasMore: boolean;
}

export interface FavouritesApiResponse {
  success: boolean;
  data?: Favourite[];
  pagination?: FavouritesPagination;
  error?: string;
}

export const FAVOURITES_PAGE_SIZE = 40;

export function normalizeFavouriteImageKey(url?: string | null): string {
  const trimmed = typeof url === "string" ? url.trim() : "";
  if (!trimmed) return "";
  try {
    const parsed = new URL(trimmed);
    return `${parsed.origin}${parsed.pathname}`.replace(/\/+$/, "").toLowerCase();
  } catch {
    return trimmed.replace(/\/+$/, "").toLowerCase();
  }
}

/** Simple true/false local flag for current session art (transformation → print-frame). */
export const FAVOURITE_IS_ALREADY_SAVED_KEY = "isAlreadySaved";
export const FAVOURITE_IS_ALREADY_SAVED_IMAGE_KEY = "isAlreadySavedImageUrl";

export function readFavouriteIsAlreadySaved(): boolean {
  try {
    return localStorage.getItem(FAVOURITE_IS_ALREADY_SAVED_KEY) === "true";
  } catch {
    return false;
  }
}

export function isFavouriteArtSavedLocally(imageUrl?: string | null): boolean {
  const key = normalizeFavouriteImageKey(imageUrl);
  if (!key || !readFavouriteIsAlreadySaved()) return false;
  try {
    const savedKey = normalizeFavouriteImageKey(
      localStorage.getItem(FAVOURITE_IS_ALREADY_SAVED_IMAGE_KEY)
    );
    return Boolean(savedKey) && savedKey === key;
  } catch {
    return false;
  }
}

/** Mark current art as already saved (`isAlreadySaved` = true) in localStorage. */
export function persistFavouriteArtSavedLocally(imageUrl?: string | null): void {
  const key = normalizeFavouriteImageKey(imageUrl);
  if (!key) return;
  try {
    localStorage.setItem(FAVOURITE_IS_ALREADY_SAVED_KEY, "true");
    localStorage.setItem(FAVOURITE_IS_ALREADY_SAVED_IMAGE_KEY, key);
    // Drop legacy array key if present.
    localStorage.removeItem("savedFavouriteArtKeys");
  } catch {
    /* ignore quota errors */
  }
}

/** Reset local flag (`isAlreadySaved` = false), e.g. when art changes. */
export function clearFavouriteArtSavedLocally(): void {
  try {
    localStorage.setItem(FAVOURITE_IS_ALREADY_SAVED_KEY, "false");
    localStorage.removeItem(FAVOURITE_IS_ALREADY_SAVED_IMAGE_KEY);
    localStorage.removeItem("savedFavouriteArtKeys");
  } catch {
    /* ignore */
  }
}

export function isFavouriteImageInList(
  imageUrl: string | null | undefined,
  favourites: Favourite[]
): boolean {
  const key = normalizeFavouriteImageKey(imageUrl);
  if (!key) return false;
  return favourites.some(
    (item) => normalizeFavouriteImageKey(item.imageUrl) === key
  );
}

export async function userAlreadyHasFavouriteImage(
  userId: string,
  imageUrl: string | null | undefined,
  accessToken?: string | null
): Promise<boolean> {
  const check = await checkFavouriteImageAlreadySaved({
    userId,
    imageUrl,
    accessToken,
  });
  return check.alreadySaved;
}

export type FavouriteSaveCheck = {
  /** True when this image should not call createFavourite again. */
  alreadySaved: boolean;
  /** Where the match was found. */
  source: "empty" | "local" | "list" | "api" | "none";
  /** True only when createFavourite should run. */
  shouldCreate: boolean;
};

/**
 * Check if an art URL is already a favourite before saving.
 * Order: empty URL → localStorage isAlreadySaved true/false → optional list → API.
 */
export async function checkFavouriteImageAlreadySaved(options: {
  userId?: string | null;
  imageUrl?: string | null;
  accessToken?: string | null;
  /** Optional Redux / already-fetched favourites to avoid an extra API call. */
  favourites?: Favourite[] | null;
}): Promise<FavouriteSaveCheck> {
  const trimmed = options.imageUrl?.trim() || "";
  if (!trimmed) {
    return { alreadySaved: true, source: "empty", shouldCreate: false };
  }

  if (isFavouriteArtSavedLocally(trimmed)) {
    return { alreadySaved: true, source: "local", shouldCreate: false };
  }

  // When a favourites list is provided (even empty), treat it as authoritative —
  // do not issue another getUserFavourites call.
  if (Array.isArray(options.favourites)) {
    if (isFavouriteImageInList(trimmed, options.favourites)) {
      persistFavouriteArtSavedLocally(trimmed);
      return { alreadySaved: true, source: "list", shouldCreate: false };
    }
    return { alreadySaved: false, source: "none", shouldCreate: true };
  }

  const userId = options.userId?.trim();
  if (!userId) {
    return { alreadySaved: false, source: "none", shouldCreate: true };
  }

  const response = await getUserFavourites(userId, {
    page: 1,
    limit: FAVOURITES_PAGE_SIZE,
    accessToken: options.accessToken,
  });
  if (!response.success) {
    return { alreadySaved: false, source: "none", shouldCreate: true };
  }

  const exists = isFavouriteImageInList(trimmed, response.data || []);
  if (exists) {
    persistFavouriteArtSavedLocally(trimmed);
    return { alreadySaved: true, source: "api", shouldCreate: false };
  }

  return { alreadySaved: false, source: "none", shouldCreate: true };
}

function asFavouriteList(value: unknown): Favourite[] {
  return Array.isArray(value) ? (value as Favourite[]) : [];
}

function parseFavouritesPayload(
  payload: any,
  page: number,
  limit: number
): { items: Favourite[]; pagination: FavouritesPagination } {
  const items = asFavouriteList(
    payload?.data ?? payload?.favourites ?? payload?.items ?? payload
  );
  const meta = payload?.pagination ?? payload?.meta ?? payload ?? {};
  const currentPage = Number(meta.page ?? meta.currentPage ?? meta.current_page ?? page) || page;
  const pageLimit =
    Number(meta.limit ?? meta.items ?? meta.perPage ?? meta.per_page ?? limit) ||
    limit;
  const total = Number(meta.total ?? payload?.total);
  const totalPages = Number(
    meta.totalPages ?? meta.total_pages ?? meta.lastPage ?? meta.last_page ?? payload?.totalPages
  );
  const hasPaginationObject = Boolean(
    payload?.pagination || payload?.meta || payload?.totalPages || payload?.total_pages
  );
  const isRawArray = Array.isArray(payload);
  const hasMoreExplicit = meta.hasMore ?? meta.has_more ?? meta.hasNext ?? meta.has_next;
  const hasMore =
    isRawArray && !hasPaginationObject
      ? false
      : typeof hasMoreExplicit === "boolean"
        ? hasMoreExplicit
        : Number.isFinite(totalPages)
          ? currentPage < totalPages
          : Number.isFinite(total)
            ? currentPage * pageLimit < total
            : items.length >= pageLimit;

  return {
    items,
    pagination: {
      page: currentPage,
      limit: pageLimit,
      total: Number.isFinite(total) ? total : undefined,
      totalPages: Number.isFinite(totalPages) ? totalPages : undefined,
      hasMore,
    },
  };
}

/** Get one page of favourites for a user (same pagination as Creative Studio shop). */
export async function getUserFavourites(
  userId: string,
  options?: { page?: number; limit?: number; accessToken?: string | null }
): Promise<FavouritesApiResponse> {
  try {
    if (!userId) {
      return {
        success: false,
        error: "User ID is required",
        data: [],
      };
    }

    const page = Math.max(1, options?.page ?? 1);
    const limit = Math.max(1, options?.limit ?? FAVOURITES_PAGE_SIZE);
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      accept: "application/json",
    };
    const token = options?.accessToken?.trim();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.get(`${favouritesApiUrl}`, {
      params: { userId, page, items: limit },
      headers,
    });

    const parsed = parseFavouritesPayload(response.data, page, limit);
    const pageItems = parsed.items.slice(0, limit);

    return {
      success: true,
      data: pageItems,
      pagination: {
        ...parsed.pagination,
        limit,
        hasMore: parsed.pagination.hasMore || parsed.items.length >= limit,
      },
    };
  } catch (error: unknown) {
    console.error("[favouritesService] Error:", error);
    return {
      success: false,
      error: getAxiosErrorMessage(error, "Network error while fetching favourites"),
      data: [],
    };
  }
}
